// Executive Challenge result screen data: the locked (or in-progress) report
// card for the contested season, framed by this GM's standing in the challenge
// group. Group data comes from the challenge backend (Phase 1: local stub, so
// usually just your own entry).

import { idb } from "../db/index.ts";
import { g, helpers } from "../util/index.ts";
import type { UpdateEvents } from "../../common/types.ts";
import * as challengeBackend from "../util/challengeBackend/index.ts";
import type { ChallengeEntry } from "../util/challengeBackend/index.ts";
import { league } from "../core/index.ts";
import getChallenge from "../core/league/getChallenge.ts";
import {
	computeChallengeLedger,
	type ChallengeLedger,
} from "../core/season/challengeLedger.ts";

const updateChallengeResult = async (
	inputs: unknown,
	updateEvents: UpdateEvents,
) => {
	if (
		updateEvents.includes("firstRun") ||
		updateEvents.includes("newPhase") ||
		updateEvents.includes("gameAttributes")
	) {
		let ch = getChallenge();
		if (!ch) {
			// Not a challenge save — nothing to show here.
			return { redirectUrl: helpers.leagueUrl([]) };
		}

		// Fail-open retry (spec §7.5): finalize locks the result locally even if
		// the backend submit fails — the only casualty is the share link (no
		// shareId). Retry whenever the result screen loads without one, and
		// persist shareId/shareExpiresAt into the challenge gameAttr on success
		// (mirrors the happy path in newPhaseAfterDraft's finalizeChallenge).
		if (ch.status === "complete" && ch.result && !ch.result.shareId) {
			try {
				const { shareId, shareExpiresAt } = await challengeBackend.submitEntry({
					code: ch.code,
					playerId: ch.playerId,
					gmScore: ch.result.gmScore,
					grade: ch.result.grade,
					draftPick: ch.result.draftPick,
					wins: ch.result.wins,
					losses: ch.result.losses,
				});
				ch = {
					...ch,
					result: { ...ch.result, shareId, shareExpiresAt },
				};
				await league.setGameAttributes({ challenge: ch });
			} catch {
				// Still offline — the screen renders the "Preparing link…" state
				// and the next visit retries again.
			}
		}

		const teamSeasons = await idb.getCopies.teamSeasons(
			{ season: ch.endSeason },
			"noCopyCache",
		);
		const ts = teamSeasons.find((row) => row.tid === ch.tid);
		const card = ts?.reportCard;

		const t = g.get("teamInfoCache")[ch.tid];
		const teamName = t ? `${t.region} ${t.name}` : "";

		// Group standing: everyone this device knows about, own entry always
		// included (fail-open — the backend may not have received the submit).
		let entries: ChallengeEntry[] = [];
		try {
			entries = await challengeBackend.getLeaderboard(ch.code);
		} catch {
			// Offline — rank against just the local entry below.
		}
		if (ch.result && !entries.some((e) => e.playerId === ch.playerId)) {
			entries.push({
				code: ch.code,
				playerId: ch.playerId,
				gmScore: ch.result.gmScore,
				grade: ch.result.grade,
				draftPick: ch.result.draftPick,
				wins: ch.result.wins,
				losses: ch.result.losses,
			});
		}
		entries.sort((a, b) => b.gmScore - a.gmScore);
		const rankIndex = entries.findIndex((e) => e.playerId === ch.playerId);
		const leader = entries[0];

		// The front office ledger — the decision story (best move, the one that
		// got away, who carried you, who anchored you). Only worth telling once
		// the season is locked.
		let ledger: ChallengeLedger = {};
		if (ch.status === "complete") {
			const players = await idb.getCopies.players(
				{ activeSeason: ch.endSeason },
				"noCopyCache",
			);
			ledger = computeChallengeLedger(players, ch);
		}

		return {
			ledger,
			status: ch.status,
			code: ch.code,
			playerId: ch.playerId,
			result: ch.result,
			card,
			teamName,
			season: ch.endSeason,
			won: ch.result?.wins ?? ts?.won ?? 0,
			lost: ch.result?.losses ?? ts?.lost ?? 0,
			projectedWins: ts?.projectedWins,
			rank: rankIndex >= 0 ? rankIndex + 1 : undefined,
			groupSize: entries.length,
			leaderPlayerId: leader?.playerId,
			leaderGap:
				leader && ch.result ? leader.gmScore - ch.result.gmScore : undefined,
		};
	}
};

export default updateChallengeResult;
